import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C } from '../constants/dashboardConstants';
import { useAuth } from '../../contexts/AuthContext';
import FindPasswordModal from './FindPasswordModal';
import styles from './AccountSettings.module.css';

function PencilIcon({ onClick, disabled }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      onClick={disabled ? undefined : onClick}
      style={{ cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }}
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={disabled ? C.g300 : C.g400} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={disabled ? C.g300 : C.g400} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function EditableInfoCard({ label, value, onSave, readOnly = false, inputPrefix = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const handleEdit = () => {
    let editValue = value || '';
    if (inputPrefix === '@' && editValue.startsWith('https://instagram.com/')) {
      editValue = '@' + editValue.replace('https://instagram.com/', '');
    }
    setDraft(editValue);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => setEditing(false);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <div className={styles.infoCardEditing}>
        <span className={styles.infoCardLabel}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {inputPrefix && (
            <span style={{
              display: 'flex', alignItems: 'center',
              height: '38px', padding: '0 10px',
              background: C.g200, border: `1px solid ${C.g200}`,
              borderRight: 'none', borderRadius: '8px 0 0 8px',
              fontFamily: 'Pretendard, sans-serif', fontSize: '16px', fontWeight: 500,
              color: C.sub, letterSpacing: '-0.04em',
            }}>{inputPrefix}</span>
          )}
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPrefix ? '아이디만 입력' : ''}
            className={styles.editInput}
            style={{ borderRadius: inputPrefix ? '0 8px 8px 0' : '8px' }}
          />
        </div>
        <div className={styles.editActions}>
          <button onClick={handleCancel} className={styles.cancelButton}>취소</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`${styles.saveButton} ${saving ? styles.saveButtonSaving : styles.saveButtonNormal}`}
          >{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    );
  }

  // 표시값 변환: URL → 간결한 형태
  let displayValue = value || '—';
  if (value) {
    if (value.startsWith('https://instagram.com/') || value.startsWith('https://www.instagram.com/')) {
      const id = value.replace(/https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/$/, '');
      displayValue = `@${id}`;
    } else if (inputPrefix === '@' && !value.startsWith('http')) {
      // 아이디만 저장된 경우 @ 붙이기
      displayValue = `@${value.replace(/^@+/, '')}`;
    } else if (value.startsWith('https://youtube.com/') || value.startsWith('https://www.youtube.com/')) {
      displayValue = value.replace(/https?:\/\/(www\.)?youtube\.com\//, '').replace(/\/$/, '');
    }
  }

  return (
    <div className={styles.infoCard}>
      <div className={styles.infoCardTopRow}>
        <span className={styles.infoCardLabel}>{label}</span>
        {!readOnly && <PencilIcon onClick={handleEdit} />}
      </div>
      <span className={styles.infoCardValue}>
        {displayValue}
      </span>
    </div>
  );
}

export default function AccountSettings({ profile, user, onSaveProfile, onSaveUser }) {
  const navigate = useNavigate();
  const { role, isTalent, isProducer } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const currentRoleLabel = role === 'TALENT' ? '배우' : '제작사';
  // 상대 역할 계정이 없을 때만 추가 버튼 표시
  const canAddTalent = role === 'PRODUCER' && !isTalent;
  const canAddProducer = role === 'TALENT' && !isProducer;
  const addAccountPath = canAddTalent ? '/onboarding/talent' : canAddProducer ? '/onboarding/producer' : null;
  const addAccountLabel = canAddTalent ? '배우 계정\n추가하기' : canAddProducer ? '제작사 계정\n추가하기' : null;

  return (
    <>
    <div className={styles.container}>

      {/* 프로필 관리 */}
      <div className={styles.section}>
        <span className={styles.sectionTitle}>프로필 관리</span>
        <div className={styles.profileCard}>
          <div className={styles.profileInfo}>
            <div className={styles.profileNameRow}>
              <span className={styles.profileName}>활성화된 프로필</span>
              <div className={styles.profileBadge}>
                <span className={styles.profileBadgeText}>{currentRoleLabel}</span>
              </div>
            </div>
            <span className={styles.profileSubtitle}>한 계정에서 배우와 제작사 프로필을<br />모두 사용할 수 있습니다</span>
          </div>
          {addAccountPath && (
            <button onClick={() => navigate(addAccountPath)} className={styles.addAccountButton}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke={C.g500} strokeWidth="1.8"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={C.g500} strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M19 8v6M22 11h-6" stroke={C.g500} strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              <span className={styles.addAccountButtonText}>{addAccountLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* 개인 정보 */}
      <div className={styles.section}>
        <span className={styles.sectionTitle}>개인 정보</span>
        <div className={styles.infoGrid}>
          <EditableInfoCard
            label="예명/활동명"
            value={profile?.stageName}
            onSave={(v) => onSaveProfile?.({ stageName: v })}
          />
          <EditableInfoCard
            label="이메일 (아이디)"
            value={user?.email || profile?.contactEmail}
            readOnly
          />
          <EditableInfoCard
            label="전화번호"
            value={(() => { const d = (user?.phone || profile?.phone || '').replace(/\D/g,''); return d.length===11?`${d.slice(0,3)}-${d.slice(3,7)}-${d.slice(7)}`:d.length===10?`${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`:(user?.phone||profile?.phone||'—'); })()}
            onSave={(v) => onSaveUser?.({ phone: v })}
            readOnly
          />
          <div className={styles.infoCard}>
            <span className={styles.infoCardLabel}>비밀번호</span>
            <button
              onClick={() => setShowPasswordModal(true)}
              style={{
                alignSelf: 'flex-start', height: '34px', padding: '0 16px',
                background: C.g200, border: 'none', borderRadius: '8px',
                cursor: 'pointer',
                fontFamily: 'Pretendard, sans-serif', fontSize: '14px',
                fontWeight: 600, color: C.bk, letterSpacing: '-0.04em',
              }}
            >
              비밀번호 변경
            </button>
          </div>
          <EditableInfoCard
            label="인스타그램"
            value={profile?.instagramUrl}
            inputPrefix="@"
            onSave={(v) => {
              const trimmed = v.trim();
              let url = trimmed;
              if (trimmed.startsWith('@')) {
                url = `https://instagram.com/${trimmed.slice(1)}`;
              } else if (trimmed && !trimmed.startsWith('http')) {
                url = `https://instagram.com/${trimmed}`;
              }
              onSaveProfile?.({ instagramUrl: url });
            }}
          />
          <EditableInfoCard
            label="유튜브"
            value={profile?.youtubeUrl}
            onSave={(v) => {
              const trimmed = v.trim();
              let url = trimmed;
              if (trimmed.startsWith('@')) {
                url = `https://youtube.com/${trimmed}`;
              } else if (trimmed && !trimmed.startsWith('http')) {
                url = `https://youtube.com/@${trimmed}`;
              }
              onSaveProfile?.({ youtubeUrl: url });
            }}
          />
        </div>
      </div>

    </div>

    {showPasswordModal && <FindPasswordModal onClose={() => setShowPasswordModal(false)} />}
    </>
  );
}
